import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Plus, PencilSimple, Trash, SignOut, ChartBar, Buildings, Trophy, Envelope, CursorClick, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { consumerAccountsEnabled } from "../context/ConsumerAuthContext";
import api, {
  CATEGORIES,
  formatGBP,
  formatGBPDecimal,
  categoryLabel,
} from "../lib/api";
import VerificationQueue from "../components/VerificationQueue";
import IntegrityQueue from "../components/IntegrityQueue";
import IngestRunsPanel from "../components/IngestRunsPanel";
import AccountSettings from "../components/AccountSettings";
import ResetPanel from "../components/ResetPanel";
import ProviderSettings from "../components/ProviderSettings";
import AdminNotificationBell from "../components/AdminNotificationBell";
import OperatorHealthCentre from "../components/OperatorHealthCentre";
import ConsumerUserManagement from "../components/ConsumerUserManagement";
import { toast } from "sonner";

const STATUSES = ["active", "closing_soon", "ended", "draft"];

const blankOperator = {
  name: "", website_url: "", logo_url: "", trust_summary: "",
  winner_visibility_score: 50, terms_clarity_score: 50,
  free_entry_clarity_score: 50, extension_policy_clarity_score: 50,
  social_proof_summary: "", known_risk_flags: [],
  operator_verification_status: "seed_candidate",
  operator_type: "operator",
  priority_tier: "secondary",
  source_url: "", terms_url: "", free_entry_url: "", trustpilot_url: "",
  manual_review_notes: "",
};

const blankComp = {
  title: "", operator_id: "", category: "cars", prize_name: "", prize_description: "",
  prize_value_estimate: "", cash_alternative_value: "", ticket_price: "",
  max_tickets: "", max_tickets_per_user: "", min_ticket_purchase: 1, draw_date: "", closing_date: "",
  free_entry_available: true, free_entry_method_summary: "",
  extension_policy_summary: "", instant_win: false, guaranteed_draw: true,
  competition_url: "", affiliate_url: "", image_url: "", status: "active", notes: "",
  terms_url: "", last_verified_at: "", data_confidence_score: 70,
  image_alt_text: "", image_source_url: "", image_matches_prize: true,
  image_verification_status: "pending", image_review_notes: "",
  // Iter 6 — structured prize spec
  prize_make: "", prize_model: "", prize_variant: "", prize_trim: "",
  prize_model_year: "", body_style: "", fuel_type: "", drivetrain: "",
  prize_color: "", prize_display_name: "", prize_type: "",
  asset_resolution_status: "pending_review",
  // Iter 9 — operator-sourced prize images
  source_competition_url: "", source_image_url: "", source_image_host: "",
  source_operator_name: "", source_operator_logo_url: "",
  source_image_status: "pending_review",
  source_image_usage_mode: "hotlink",
  image_rights_status: "unknown",
  source_image_review_notes: "",
  source_image_attribution_label: "",
};

export default function AdminDashboard() {
  const { user, ready, logout, sessionState, retrySession } = useAuth();
  const navigate = useNavigate();
  const initialTab = (() => {
    if (typeof window === "undefined") return "competitions";
    return new URLSearchParams(window.location.search).get("tab") || "competitions";
  })();
  const [tab, setTab] = useState(initialTab);
  const [stats, setStats] = useState(null);
  const [operators, setOperators] = useState([]);
  const [comps, setComps] = useState([]);
  const [signups, setSignups] = useState([]);
  const [statsState, setStatsState] = useState("idle");
  const [tabDataState, setTabDataState] = useState("idle");
  const [lastLoadError, setLastLoadError] = useState("");
  const statsRequestId = useRef(0);
  const tabRequestId = useRef(0);
  const [modal, setModal] = useState(null); // { type, data }

  const loadStats = useCallback(async ({ signal } = {}) => {
    const requestId = ++statsRequestId.current;
    setStatsState("loading");
    try {
      const response = await api.get("/admin/stats", { signal });
      if (signal?.aborted || requestId !== statsRequestId.current) return;
      setStats(response.data);
      setStatsState("ready");
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      if (requestId !== statsRequestId.current) return;
      setStatsState("error");
      setLastLoadError(error?.response?.data?.detail || error?.message || "Stats unavailable");
    }
  }, []);

  const loadTabData = useCallback(async (targetTab, { signal } = {}) => {
    const requestId = ++tabRequestId.current;
    const isCurrent = () => (
      !signal?.aborted && requestId === tabRequestId.current
    );
    setTabDataState("loading");
    try {
      if (targetTab === "competitions") {
        const [operatorResponse, competitionResponse] = await Promise.all([
          api.get("/admin/operators", { signal }),
          api.get("/admin/competitions", { signal }),
        ]);
        if (!isCurrent()) return;
        setOperators(operatorResponse.data);
        setComps(competitionResponse.data);
      } else if (targetTab === "operators") {
        const response = await api.get("/admin/operators", { signal });
        if (!isCurrent()) return;
        setOperators(response.data);
      } else if (targetTab === "signups") {
        const response = await api.get("/admin/email-signups", { signal });
        if (!isCurrent()) return;
        setSignups(response.data);
      }
      if (isCurrent()) setTabDataState("ready");
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") return;
      if (!isCurrent()) return;
      setTabDataState("error");
      setLastLoadError(error?.response?.data?.detail || error?.message || "Admin data unavailable");
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.allSettled([
      loadStats(),
      loadTabData(tab),
    ]);
  }, [loadStats, loadTabData, tab]);

  useEffect(() => {
    if (!ready || sessionState === "unavailable" || user?.role !== "admin") return undefined;
    const controller = new AbortController();
    loadStats({ signal: controller.signal });
    return () => controller.abort();
  }, [loadStats, ready, sessionState, user?.role]);

  useEffect(() => {
    if (!ready || sessionState === "unavailable" || user?.role !== "admin") return undefined;
    const controller = new AbortController();
    loadTabData(tab, { signal: controller.signal });
    return () => controller.abort();
  }, [loadTabData, ready, sessionState, tab, user?.role]);

  if (sessionState === "unavailable") {
    return (
      <div
        className="min-h-[80vh] flex flex-col items-center justify-center gap-4 px-6 py-12 text-sm text-white/70"
        data-testid="admin-auth-unavailable"
        data-admin-auth-state="unavailable"
        role="status"
      >
        <p>We couldn’t check your sign-in. Try again.</p>
        <button type="button" className="btn-gold rounded px-5 py-2" onClick={retrySession} data-testid="admin-session-retry">
          Try again
        </button>
      </div>
    );
  }

  if (ready && (!user || user.role !== "admin")) {
    return (
      <div data-testid="admin-auth-required" data-admin-auth-state="required">
        <Navigate to="/admin/login" replace />
      </div>
    );
  }
  if (!ready) {
    return (
      <div
        className="p-12 text-[var(--cc-text-muted)]"
        data-testid="admin-auth-resolving"
        data-admin-auth-state="resolving"
      >
        Resolving admin session...
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div
      className="max-w-7xl mx-auto px-6 lg:px-8 py-10"
      data-testid="admin-authenticated"
      data-admin-auth-state="authenticated"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline text-[var(--cc-gold)] mb-1">Admin</div>
          <h1 className="font-display text-3xl font-semibold">Dashboard</h1>
          <p className="text-xs text-[var(--cc-text-muted)] mt-1">Signed in as {user.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <AdminNotificationBell />
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </div>

      {/* Stats */}
      <div
        className="sr-only"
        role="status"
        data-testid={
          statsState === "ready"
            ? "admin-dashboard-secondary-ready"
            : statsState === "error"
            ? "admin-dashboard-secondary-failed"
            : "admin-dashboard-secondary-loading"
        }
        data-last-api-error={lastLoadError}
      >
        Dashboard metrics {statsState}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
        <StatCard
          icon={Trophy}
          label="Competitions"
          value={stats ? stats.competitions_total : statsState === "error" ? "Unavailable" : "Loading"}
          sub={stats ? `${stats.competitions_active} active` : undefined}
        />
        <StatCard icon={Buildings} label="Operators" value={stats ? stats.operators_total : statsState === "error" ? "Unavailable" : "Loading"} />
        <StatCard icon={Envelope} label="Signups" value={stats ? stats.email_signups_total : statsState === "error" ? "Unavailable" : "Loading"} />
        <StatCard icon={CursorClick} label="Outbound" value={stats ? stats.outbound_clicks_total : statsState === "error" ? "Unavailable" : "Loading"} />
        <StatCard
          icon={ChartBar}
          label="Active rate"
          value={stats
            ? `${Math.round((stats.competitions_active / Math.max(1, stats.competitions_total)) * 100)}%`
            : statsState === "error" ? "Unavailable" : "Loading"}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto">
        {[
          { k: "competitions", label: "Competitions" },
          { k: "integrity", label: "Data integrity" },
          { k: "ingest", label: "Auto-ingest" },
          { k: "health", label: "Health" },
          { k: "operators", label: "Operators" },
          { k: "verification_queue", label: "Verification Queue" },
          { k: "signups", label: "Email signups" },
          { k: "providers", label: "Providers" },
          ...(consumerAccountsEnabled()
            ? [{ k: "consumer_users", label: "Consumer users" }]
            : []),
          { k: "reset", label: "Reset" },
          { k: "account", label: "Account" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.k ? "border-[var(--cc-gold)] text-white" : "border-transparent text-white/60 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "competitions" && tabDataState === "loading" && (
        <AdminDataState label="Loading competition controls..." />
      )}
      {tab === "competitions" && tabDataState === "error" && (
        <AdminDataState label="Competition controls are unavailable." error />
      )}
      {tab === "competitions" && tabDataState === "ready" && (
        <CompetitionsTab
          comps={comps}
          operators={operators}
          onAdd={() => setModal({ type: "comp", data: { ...blankComp, operator_id: operators[0]?.id || "" } })}
          onEdit={(c) => setModal({ type: "comp", data: { ...c } })}
          onDelete={async (c) => {
            if (!window.confirm(`Delete "${c.title}"?`)) return;
            await api.delete(`/admin/competitions/${c.id}`);
            toast.success("Deleted");
            refresh();
          }}
        />
      )}
      {tab === "operators" && tabDataState === "loading" && (
        <AdminDataState label="Loading operator controls..." />
      )}
      {tab === "operators" && tabDataState === "error" && (
        <AdminDataState label="Operator controls are unavailable." error />
      )}
      {tab === "operators" && tabDataState === "ready" && (
        <OperatorsTab
          ops={operators}
          onRefresh={refresh}
          onAdd={() => setModal({ type: "op", data: { ...blankOperator } })}
          onEdit={(o) => setModal({ type: "op", data: { ...o } })}
          onDelete={async (o) => {
            if (!window.confirm(`Delete "${o.name}"?`)) return;
            await api.delete(`/admin/operators/${o.id}`);
            toast.success("Deleted");
            refresh();
          }}
        />
      )}
      {tab === "verification_queue" && (
        <VerificationQueue />
      )}
      {tab === "integrity" && (
        <IntegrityQueue />
      )}
      {tab === "ingest" && (
        <IngestRunsPanel />
      )}
      {tab === "health" && (
        <OperatorHealthCentre />
      )}
      {tab === "account" && (
        <AccountSettings />
      )}
      {tab === "providers" && (
        <ProviderSettings />
      )}
      {tab === "consumer_users" && (
        <ConsumerUserManagement />
      )}
      {tab === "reset" && (
        <ResetPanel />
      )}
      {tab === "signups" && tabDataState === "loading" && (
        <AdminDataState label="Loading email signups..." />
      )}
      {tab === "signups" && tabDataState === "error" && (
        <AdminDataState label="Email signups are unavailable." error />
      )}
      {tab === "signups" && tabDataState === "ready" && (
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60">
              <tr><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Source</th><th className="text-left px-4 py-3">Date</th></tr>
            </thead>
            <tbody>
              {signups.map((s) => (
                <tr key={s.id} className="border-t border-white/5">
                  <td className="px-4 py-3 font-mono-num">{s.email}</td>
                  <td className="px-4 py-3 text-white/60">{s.source}</td>
                  <td className="px-4 py-3 text-white/40">{new Date(s.created_at).toLocaleString("en-GB")}</td>
                </tr>
              ))}
              {signups.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-white/40">No signups yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && modal.type === "comp" && (
        <CompetitionModal
          data={modal.data}
          operators={operators}
          onClose={() => setModal(null)}
          onReResolve={async (compId) => {
            try {
              const { data } = await api.post(`/admin/competitions/${compId}/re-resolve-asset`);
              toast.success(`Asset re-resolved → ${data.asset_resolution_status}`);
              setModal({ type: "comp", data });
              refresh();
            } catch (e) {
              toast.error("Re-resolve failed: " + (e.response?.data?.detail || e.message));
            }
          }}
          onSuggestSource={async (compId, sourceUrl) => {
            if (!sourceUrl) {
              toast.error("Set the source competition URL first.");
              return;
            }
            try {
              const { data } = await api.post(
                `/admin/competitions/${compId}/suggest-source-image`,
                { source_competition_url: sourceUrl },
              );
              toast.success("Source image captured · review pending.");
              setModal({ type: "comp", data: data.competition });
              refresh();
            } catch (e) {
              toast.error("Suggest failed: " + (e.response?.data?.detail || e.message));
            }
          }}
          onSave={async (payload) => {
            try {
              if (payload.id) {
                await api.put(`/admin/competitions/${payload.id}`, payload);
              } else {
                await api.post("/admin/competitions", payload);
              }
              toast.success("Saved");
              setModal(null);
              refresh();
            } catch (e) {
              toast.error("Save failed: " + (e.response?.data?.detail || e.message));
            }
          }}
        />
      )}
      {modal && modal.type === "op" && (
        <OperatorModal
          data={modal.data}
          onClose={() => setModal(null)}
          onSave={async (payload) => {
            try {
              if (payload.id) await api.put(`/admin/operators/${payload.id}`, payload);
              else await api.post("/admin/operators", payload);
              toast.success("Saved");
              setModal(null);
              refresh();
            } catch (e) {
              toast.error("Save failed: " + (e.response?.data?.detail || e.message));
            }
          }}
        />
      )}
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-lg border border-white/10 p-4 bg-white/[0.02]">
    <div className="flex items-center gap-2 overline text-white/40 mb-2"><Icon size={12} weight="fill" /> {label}</div>
    <div className="font-mono-num text-2xl font-semibold">{value}</div>
    {sub && <div className="text-[11px] text-white/40 mt-1">{sub}</div>}
  </div>
);

const AdminDataState = ({ label, error = false }) => (
  <div
    className={`rounded-lg border p-6 text-sm ${
      error
        ? "border-red-400/25 bg-red-400/10 text-red-100"
        : "border-white/10 bg-white/[0.03] text-white/55"
    }`}
    role="status"
  >
    {label}
  </div>
);

const CompetitionsTab = ({ comps, operators, onAdd, onEdit, onDelete }) => {
  const opMap = Object.fromEntries(operators.map((o) => [o.id, o.name]));
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={onAdd} data-testid="admin-add-competition" className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5">
          <Plus size={14} weight="bold" /> New competition
        </button>
      </div>
      <div className="rounded-lg border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-white/5 text-white/60">
            <tr>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Operator</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Ticket</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Clicks</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {comps.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`admin-comp-row-${c.id}`}>
                <td className="px-4 py-3 max-w-xs truncate">{c.title}</td>
                <td className="px-4 py-3 text-white/60">{opMap[c.operator_id] || "—"}</td>
                <td className="px-4 py-3 text-white/60">{categoryLabel(c.category)}</td>
                <td className="px-4 py-3 font-mono-num">{formatGBPDecimal(c.ticket_price)}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-[11px] bg-white/10 text-white/80">{c.status}</span></td>
                <td className="px-4 py-3 font-mono-num">{c.click_count || 0}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => onEdit(c)} data-testid={`admin-edit-${c.id}`} className="text-white/60 hover:text-white p-1.5"><PencilSimple size={16} /></button>
                  <button onClick={() => onDelete(c)} data-testid={`admin-delete-${c.id}`} className="text-red-400 hover:text-red-300 p-1.5"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {comps.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">No competitions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OperatorsTab = ({ ops, onAdd, onEdit, onDelete, onRefresh }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");

  const filtered = ops.filter((o) => {
    if (q && !o.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (status !== "all" && o.operator_verification_status !== status) return false;
    return true;
  });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyBulk = async () => {
    if (!bulkStatus || selected.size === 0) return;
    try {
      await api.post("/admin/operators/bulk-update", {
        operator_ids: Array.from(selected),
        verification_status: bulkStatus,
      });
      toast.success(`Updated ${selected.size} operator(s)`);
      setSelected(new Set());
      setBulkStatus("");
      onRefresh && onRefresh();
    } catch (e) {
      toast.error("Bulk update failed");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search operators..."
          data-testid="admin-operator-search"
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-[var(--cc-gold)] flex-1 min-w-[200px]"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="admin-operator-status-filter"
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="verified">Verified</option>
          <option value="partial">Partial</option>
          <option value="seed_candidate">Seed candidate</option>
          <option value="archived">Archived</option>
        </select>
        {selected.size > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              data-testid="admin-bulk-status"
              className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
            >
              <option value="">Bulk mark as...</option>
              <option value="verified">Verified</option>
              <option value="partial">Partial</option>
              <option value="seed_candidate">Seed candidate</option>
              <option value="archived">Archived</option>
            </select>
            <button
              onClick={applyBulk}
              disabled={!bulkStatus}
              data-testid="admin-bulk-apply"
              className="btn-gold px-3 py-2 rounded text-xs disabled:opacity-50"
            >
              Apply ({selected.size})
            </button>
          </div>
        )}
        <button onClick={onAdd} data-testid="admin-add-operator" className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-1.5">
          <Plus size={14} weight="bold" /> New operator
        </button>
      </div>
      <div className="rounded-lg border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-white/5 text-white/60">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every((o) => selected.has(o.id))}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(new Set(filtered.map((o) => o.id)));
                    else setSelected(new Set());
                  }}
                />
              </th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Tier</th>
              <th className="text-left px-4 py-3">Trust</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => {
              const trust = Math.round((o.winner_visibility_score + o.terms_clarity_score + o.free_entry_clarity_score + o.extension_policy_clarity_score) / 4);
              return (
                <tr key={o.id} className="border-t border-white/5" data-testid={`admin-op-row-${o.id}`}>
                  <td className="px-3 py-3">
                    <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} data-testid={`admin-op-check-${o.id}`} />
                  </td>
                  <td className="px-4 py-3 font-semibold">{o.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                      o.operator_verification_status === "verified" ? "bg-emerald-500/20 text-emerald-300" :
                      o.operator_verification_status === "partial" ? "bg-amber-500/20 text-amber-300" :
                      o.operator_verification_status === "archived" ? "bg-slate-500/20 text-slate-400" :
                      "bg-slate-500/30 text-slate-300"
                    }`}>
                      {o.operator_verification_status || "seed_candidate"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/70 text-xs">{o.operator_type || "operator"}</td>
                  <td className="px-4 py-3 text-white/70 text-xs">{o.priority_tier || "secondary"}</td>
                  <td className="px-4 py-3 font-mono-num">{trust}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onEdit(o)} className="text-white/60 hover:text-white p-1.5"><PencilSimple size={16} /></button>
                    <button onClick={() => onDelete(o)} className="text-red-400 hover:text-red-300 p-1.5"><Trash size={16} /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">No operators match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
    <div className="bg-[var(--cc-card-dark)] rounded-lg border border-white/10 w-full max-w-2xl my-8">
      <div className="flex items-center justify-between p-5 border-b border-white/10">
        <h3 className="font-display text-xl">{title}</h3>
        <button onClick={onClose} className="text-white/60 hover:text-white text-sm">Close</button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const TextField = ({ label, value, onChange, type = "text", required, testId }) => (
  <label className="block">
    <div className="overline text-white/50 mb-1.5">{label}{required && " *"}</div>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
      required={required}
      data-testid={testId}
      className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-[var(--cc-gold)]"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, required, testId }) => (
  <label className="block">
    <div className="overline text-white/50 mb-1.5">{label}{required && " *"}</div>
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testId}
      className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white outline-none focus:border-[var(--cc-gold)]"
    >
      {options.map((o) => <option key={o.value} value={o.value} className="bg-[var(--cc-navy-2)] text-white">{o.label}</option>)}
    </select>
  </label>
);

const TextArea = ({ label, value, onChange, rows = 2, testId }) => (
  <label className="block">
    <div className="overline text-white/50 mb-1.5">{label}</div>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testId}
      className="w-full bg-white/5 border border-white/15 rounded px-3 py-2 text-sm outline-none focus:border-[var(--cc-gold)] resize-none"
    />
  </label>
);

const CheckField = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    {label}
  </label>
);

const CompetitionModal = ({ data, operators, onClose, onSave, onReResolve, onSuggestSource, onValidateUrl, onSetDataStatus }) => {
  const [form, setForm] = useState(data);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Convert empty strings to null where needed
    ["prize_value_estimate", "cash_alternative_value", "max_tickets", "max_tickets_per_user", "tickets_sold_estimate", "min_ticket_purchase", "data_confidence_score", "prize_model_year"].forEach((k) => {
      if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
      else payload[k] = Number(payload[k]);
    });
    if (payload.ticket_price === "" || payload.ticket_price == null) payload.ticket_price = 0;
    else payload.ticket_price = Number(payload.ticket_price);
    ["draw_date", "closing_date", "last_verified_at"].forEach((k) => {
      if (payload[k]) payload[k] = new Date(payload[k]).toISOString();
      else payload[k] = null;
    });
    // Empty-string prize_type would fail the Pydantic Literal check — clear it.
    if (payload.prize_type === "") payload.prize_type = null;
    onSave(payload);
  };

  return (
    <Modal onClose={onClose} title={form.id ? "Edit competition" : "New competition"}>
      <form onSubmit={submit} className="space-y-4" data-testid="competition-form">
        <TextField label="Title" value={form.title} onChange={(v) => update("title", v)} required testId="comp-title" />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Operator"
            value={form.operator_id}
            onChange={(v) => update("operator_id", v)}
            required
            options={operators.map((o) => ({ value: o.id, label: o.name }))}
            testId="comp-operator"
          />
          <SelectField
            label="Category"
            value={form.category}
            onChange={(v) => update("category", v)}
            required
            options={CATEGORIES.map((c) => ({ value: c, label: categoryLabel(c) }))}
          />
        </div>
        <TextField label="Prize name" value={form.prize_name} onChange={(v) => update("prize_name", v)} required />
        <TextArea label="Prize description" value={form.prize_description} onChange={(v) => update("prize_description", v)} rows={2} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Ticket price (£)" type="number" value={form.ticket_price} onChange={(v) => update("ticket_price", v)} required testId="comp-ticket-price" />
          <TextField label="Max tickets" type="number" value={form.max_tickets} onChange={(v) => update("max_tickets", v)} testId="comp-max-tickets" />
          <TextField label="Max tickets per user" type="number" value={form.max_tickets_per_user} onChange={(v) => update("max_tickets_per_user", v)} />
          <TextField label="Min ticket purchase" type="number" value={form.min_ticket_purchase} onChange={(v) => update("min_ticket_purchase", v)} />
          <TextField label="Prize value (£)" type="number" value={form.prize_value_estimate} onChange={(v) => update("prize_value_estimate", v)} />
          <TextField label="Cash alternative (£)" type="number" value={form.cash_alternative_value} onChange={(v) => update("cash_alternative_value", v)} />
          <TextField label="Draw date" type="datetime-local" value={form.draw_date ? new Date(form.draw_date).toISOString().slice(0, 16) : ""} onChange={(v) => update("draw_date", v)} />
          <TextField label="Closing date" type="datetime-local" value={form.closing_date ? new Date(form.closing_date).toISOString().slice(0, 16) : ""} onChange={(v) => update("closing_date", v)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <CheckField label="Free entry available" value={form.free_entry_available} onChange={(v) => update("free_entry_available", v)} />
          <SelectField label="Status" value={form.status} onChange={(v) => update("status", v)} options={STATUSES.map((s) => ({ value: s, label: s }))} />
        </div>
        <TextArea label="Free entry method" value={form.free_entry_method_summary} onChange={(v) => update("free_entry_method_summary", v)} rows={2} />
        <TextArea label="Extension policy" value={form.extension_policy_summary} onChange={(v) => update("extension_policy_summary", v)} rows={2} />
        <TextField label="Competition URL" value={form.competition_url} onChange={(v) => update("competition_url", v)} required />
        <TextField label="Affiliate URL (optional)" value={form.affiliate_url || ""} onChange={(v) => update("affiliate_url", v)} />

        {/* P0 — Source URL validation & data status (iter 11) */}
        {form.id && (
          <div className="mt-3 pt-3 border-t border-white/10" data-testid="data-integrity-section">
            <div className="overline text-[var(--cc-gold)] mb-2">Data integrity</div>
            <div className="grid grid-cols-2 gap-3">
              <SelectField
                label="Competition data status"
                value={form.competition_data_status || "source_unverified"}
                onChange={(v) => update("competition_data_status", v)}
                testId="comp-data-status"
                options={[
                  { value: "verified_live", label: "Verified live" },
                  { value: "source_pending_review", label: "Source pending review" },
                  { value: "source_unverified", label: "Source unverified" },
                  { value: "expired", label: "Expired" },
                  { value: "broken_link", label: "Broken link" },
                  { value: "seed_demo", label: "Seed / demo" },
                  { value: "archived", label: "Archived" },
                  { value: "hidden", label: "Hidden" },
                ]}
              />
              <div>
                <div className="overline text-white/50 mb-1.5">Last validation</div>
                <div className="text-xs text-white/70">
                  Status: <span className="font-semibold text-white">{form.source_url_validation_status || "pending_review"}</span>
                </div>
                <div className="text-[11px] text-white/50">
                  HTTP {form.source_url_http_status ?? "—"} ·
                  domain match {form.source_url_domain_match === true ? "✓" : form.source_url_domain_match === false ? "✗" : "—"} ·
                  prize {form.source_url_prize_match_score ?? "—"}
                </div>
                {form.source_url_final_url && (
                  <div className="text-[11px] text-white/40 truncate max-w-md">→ {form.source_url_final_url}</div>
                )}
                {form.source_url_validation_notes && (
                  <div className="text-[11px] text-white/40 mt-1">{form.source_url_validation_notes}</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => onValidateUrl && onValidateUrl(form.id)}
                data-testid="validate-source-url-btn"
                className="px-3 py-1.5 text-xs font-semibold rounded bg-[var(--cc-gold)] text-[var(--cc-navy)] hover:bg-[var(--cc-bronze)] hover:text-white transition-colors"
              >
                Validate source URL
              </button>
              <button
                type="button"
                onClick={() => onSetDataStatus && onSetDataStatus(form.id, "verified_live")}
                data-testid="promote-verified-live-btn"
                className="px-3 py-1.5 text-xs font-semibold rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30"
              >
                Mark verified live
              </button>
              <button
                type="button"
                onClick={() => onSetDataStatus && onSetDataStatus(form.id, "hidden")}
                data-testid="hide-comp-btn"
                className="px-3 py-1.5 text-xs font-semibold rounded bg-slate-500/30 text-slate-200 hover:bg-slate-500/50"
              >
                Hide from public
              </button>
            </div>
          </div>
        )}

        <TextField label="Image URL" value={form.image_url || ""} onChange={(v) => update("image_url", v)} testId="comp-image-url" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Image alt text" value={form.image_alt_text || ""} onChange={(v) => update("image_alt_text", v)} testId="comp-image-alt" />
          <TextField label="Image source URL" value={form.image_source_url || ""} onChange={(v) => update("image_source_url", v)} testId="comp-image-source" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Image verification status"
            value={form.image_verification_status || "pending"}
            onChange={(v) => update("image_verification_status", v)}
            testId="comp-image-status"
            options={[
              { value: "verified_match", label: "Verified match" },
              { value: "representative", label: "Representative" },
              { value: "pending", label: "Pending" },
              { value: "mismatch_flagged", label: "Mismatch flagged" },
            ]}
          />
          <CheckField
            label="Image matches prize"
            value={form.image_matches_prize !== false}
            onChange={(v) => update("image_matches_prize", v)}
          />
        </div>
        <TextArea label="Image review notes" value={form.image_review_notes || ""} onChange={(v) => update("image_review_notes", v)} rows={2} />

        {/* Iter 6 — Prize Asset Intelligence */}
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="prize-asset-section">
          <div className="overline text-[var(--cc-gold)] mb-2">Prize asset intelligence</div>
          <p className="text-[11px] text-white/50 mb-3 leading-relaxed">
            Structured prize spec drives the asset resolver. If the resolver can&apos;t find a confident match in its curated registry, the public card falls back to a clean placeholder instead of a wrong vehicle.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Prize type"
              value={form.prize_type || ""}
              onChange={(v) => update("prize_type", v)}
              testId="comp-prize-type"
              options={[
                { value: "", label: "(unset)" },
                { value: "car", label: "Car" },
                { value: "watch", label: "Watch" },
                { value: "cash", label: "Cash" },
                { value: "tech", label: "Tech" },
                { value: "gaming", label: "Gaming" },
                { value: "lifestyle", label: "Lifestyle" },
                { value: "luxury", label: "Luxury" },
                { value: "other", label: "Other" },
              ]}
            />
            <TextField label="Display name" value={form.prize_display_name || ""} onChange={(v) => update("prize_display_name", v)} testId="comp-prize-display-name" />
            <TextField label="Make" value={form.prize_make || ""} onChange={(v) => update("prize_make", v)} testId="comp-prize-make" />
            <TextField label="Model" value={form.prize_model || ""} onChange={(v) => update("prize_model", v)} testId="comp-prize-model" />
            <TextField label="Variant" value={form.prize_variant || ""} onChange={(v) => update("prize_variant", v)} testId="comp-prize-variant" />
            <TextField label="Trim (optional)" value={form.prize_trim || ""} onChange={(v) => update("prize_trim", v)} />
            <TextField label="Model year" type="number" value={form.prize_model_year || ""} onChange={(v) => update("prize_model_year", v)} />
            <TextField label="Body style" value={form.body_style || ""} onChange={(v) => update("body_style", v)} />
          </div>
          <div className="mt-3">
            <SelectField
              label="Asset resolution status (admin override)"
              value={form.asset_resolution_status || "pending_review"}
              onChange={(v) => update("asset_resolution_status", v)}
              testId="comp-asset-status"
              options={[
                { value: "pending_review", label: "Pending review (resolver decides)" },
                { value: "exact_match", label: "Exact match (admin-verified)" },
                { value: "close_match", label: "Close match / representative" },
                { value: "generated_render", label: "Generated render" },
                { value: "placeholder", label: "Placeholder only" },
                { value: "mismatch_blocked", label: "Mismatch — block image" },
              ]}
            />
            <p className="text-[10px] text-white/40 mt-1 leading-relaxed">
              Leave as &quot;Pending review&quot; to let the resolver re-decide on next read.
              Override to lock the public outcome.
            </p>
          </div>
          {form.id && onReResolve && (
            <div className="mt-3 rounded-md bg-white/[0.04] border border-white/10 p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="overline text-white/50 mb-0.5">Current resolver outcome</div>
                <div className="text-xs text-white/80 truncate">
                  {form.asset_resolution_status || "pending_review"}
                  {form.asset_confidence_score != null && (
                    <span className="text-white/40"> · conf {form.asset_confidence_score}</span>
                  )}
                  {form.asset_last_resolved_at && (
                    <span className="text-white/40"> · {new Date(form.asset_last_resolved_at).toLocaleString()}</span>
                  )}
                </div>
                {form.image_url ? (
                  <a href={form.image_url} target="_blank" rel="noreferrer" className="text-[11px] text-[var(--cc-gold)] underline truncate block max-w-md">
                    {form.image_url}
                  </a>
                ) : (
                  <div className="text-[11px] text-white/40">No image will be served — placeholder.</div>
                )}
              </div>
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="resolved"
                  className="h-14 w-20 object-cover rounded border border-white/10"
                  data-testid="resolver-thumb"
                />
              )}
              <button
                type="button"
                data-testid="re-resolve-asset-btn"
                onClick={() => onReResolve(form.id)}
                className="flex-none px-3 py-1.5 text-xs font-semibold rounded bg-[var(--cc-gold)] text-[var(--cc-navy)] hover:bg-[var(--cc-bronze)] hover:text-white transition-colors"
              >
                Re-resolve
              </button>
            </div>
          )}
        </div>

        {/* Iter 9 — Source image & attribution */}
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="source-image-section">
          <div className="overline text-[var(--cc-gold)] mb-2">Source image &amp; attribution</div>
          <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2 mb-3 text-[11px] text-amber-200 leading-relaxed">
            ⚠️ Only use source images from the operator&apos;s own listing page or where you
            have permission/comfort to display them. Attribution and linking do not
            automatically grant image rights.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Source competition URL" value={form.source_competition_url || ""} onChange={(v) => update("source_competition_url", v)} testId="comp-source-url" />
            <TextField label="Affiliate URL" value={form.affiliate_url || ""} onChange={(v) => update("affiliate_url", v)} testId="comp-affiliate-url" />
          </div>
          <TextField label="Source image URL" value={form.source_image_url || ""} onChange={(v) => update("source_image_url", v)} testId="comp-source-image-url" />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Source operator name" value={form.source_operator_name || ""} onChange={(v) => update("source_operator_name", v)} testId="comp-source-operator-name" />
            <TextField label="Attribution label (overlay copy)" value={form.source_image_attribution_label || ""} onChange={(v) => update("source_image_attribution_label", v)} testId="comp-source-attribution-label" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SelectField
              label="Source image status"
              value={form.source_image_status || "pending_review"}
              onChange={(v) => update("source_image_status", v)}
              testId="comp-source-image-status"
              options={[
                { value: "pending_review", label: "Pending review" },
                { value: "captured", label: "Captured" },
                { value: "verified", label: "Verified" },
                { value: "blocked", label: "Blocked" },
                { value: "broken", label: "Broken" },
                { value: "expired", label: "Expired" },
              ]}
            />
            <SelectField
              label="Usage mode"
              value={form.source_image_usage_mode || "hotlink"}
              onChange={(v) => update("source_image_usage_mode", v)}
              testId="comp-source-usage-mode"
              options={[
                { value: "hotlink", label: "Hotlink" },
                { value: "cached_with_permission", label: "Cached (with permission)" },
                { value: "manual_permission_required", label: "Manual permission required" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
            <SelectField
              label="Image rights"
              value={form.image_rights_status || "unknown"}
              onChange={(v) => update("image_rights_status", v)}
              testId="comp-image-rights-status"
              options={[
                { value: "unknown", label: "Unknown" },
                { value: "operator_owned", label: "Operator owned" },
                { value: "permission_granted", label: "Permission granted" },
                { value: "public_or_licensed", label: "Public / licensed" },
                { value: "do_not_use", label: "Do not use" },
              ]}
            />
          </div>
          <TextArea label="Source image review notes" value={form.source_image_review_notes || ""} onChange={(v) => update("source_image_review_notes", v)} rows={2} />

          {form.id && onSuggestSource && (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                data-testid="suggest-source-image-btn"
                onClick={() => onSuggestSource(form.id, form.source_competition_url)}
                className="px-3 py-1.5 text-xs font-semibold rounded bg-white/10 border border-white/15 text-white hover:bg-white/15 transition-colors"
              >
                Suggest image from source page
              </button>
              <span className="text-[10px] text-white/40">
                Fetches og:image / twitter:image. Stored as pending_review — won&apos;t publish until you verify.
              </span>
            </div>
          )}

          {form.source_image_url && (
            <div className="mt-3 rounded-md bg-white/[0.04] border border-white/10 p-2 flex items-center gap-3">
              <img
                src={form.source_image_url}
                alt="source preview"
                className="h-16 w-24 object-cover rounded border border-white/10"
                data-testid="source-image-preview"
              />
              <div className="text-[11px] text-white/60 leading-relaxed flex-1 min-w-0">
                <div className="font-semibold text-white/80 truncate">{form.source_image_url}</div>
                <div>
                  Status <span className="text-white/80">{form.source_image_status}</span> ·
                  Usage <span className="text-white/80">{form.source_image_usage_mode}</span> ·
                  Rights <span className="text-white/80">{form.image_rights_status}</span>
                </div>
                <div className="text-white/40 mt-0.5">
                  Public visibility requires status=captured/verified, rights ≠ do_not_use, and usage in (hotlink, cached_with_permission).
                </div>
              </div>
            </div>
          )}
        </div>

        <TextField label="Terms URL (optional)" value={form.terms_url || ""} onChange={(v) => update("terms_url", v)} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Last verified at" type="datetime-local" value={form.last_verified_at ? new Date(form.last_verified_at).toISOString().slice(0, 16) : ""} onChange={(v) => update("last_verified_at", v)} />
          <TextField label="Data confidence (0-100)" type="number" value={form.data_confidence_score} onChange={(v) => update("data_confidence_score", v)} />
        </div>
        <TextArea label="Notes" value={form.notes} onChange={(v) => update("notes", v)} rows={2} />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-white/70">Cancel</button>
          <button type="submit" data-testid="comp-save-btn" className="btn-gold px-5 py-2 rounded text-sm">Save</button>
        </div>
      </form>
    </Modal>
  );
};

const OperatorModal = ({ data, onClose, onSave }) => {
  const [form, setForm] = useState({ ...data, known_risk_flags: (data.known_risk_flags || []).join(", ") });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    payload.known_risk_flags = (payload.known_risk_flags || "")
      .split(",").map((x) => x.trim()).filter(Boolean);
    // P0: normalise alias domains list from the editable string field
    if (payload.known_alias_domains_str !== undefined) {
      payload.known_alias_domains = (payload.known_alias_domains_str || "")
        .split(",").map((x) => x.trim().toLowerCase()).filter(Boolean);
      delete payload.known_alias_domains_str;
    }
    if (payload.canonical_domain) {
      payload.canonical_domain = payload.canonical_domain.trim().toLowerCase();
    }
    // Iter 12 — normalise allow/blocklist string fields
    for (const k of ["url_patterns_allowlist", "url_patterns_blocklist"]) {
      const s = payload[`${k}_str`];
      if (s !== undefined) {
        payload[k] = (s || "").split(",").map((x) => x.trim()).filter(Boolean);
        delete payload[`${k}_str`];
      }
    }
    if (payload.rate_limit_seconds !== undefined && payload.rate_limit_seconds !== "") {
      payload.rate_limit_seconds = Number(payload.rate_limit_seconds);
    }
    ["winner_visibility_score", "terms_clarity_score", "free_entry_clarity_score", "extension_policy_clarity_score"].forEach((k) => {
      payload[k] = Number(payload[k] || 0);
    });
    onSave(payload);
  };

  return (
    <Modal onClose={onClose} title={form.id ? "Edit operator" : "New operator"}>
      <form onSubmit={submit} className="space-y-4" data-testid="operator-form">
        <TextField label="Name" value={form.name} onChange={(v) => update("name", v)} required />
        <TextField label="Website URL" value={form.website_url} onChange={(v) => update("website_url", v)} required />
        <div className="grid grid-cols-3 gap-3">
          <SelectField
            label="Verification status"
            value={form.operator_verification_status || "seed_candidate"}
            onChange={(v) => update("operator_verification_status", v)}
            testId="op-verification-status"
            options={[
              { value: "verified", label: "Verified" },
              { value: "partial", label: "Partial" },
              { value: "seed_candidate", label: "Seed candidate" },
              { value: "archived", label: "Archived" },
            ]}
          />
          <SelectField
            label="Operator type"
            value={form.operator_type || "operator"}
            onChange={(v) => update("operator_type", v)}
            options={[
              { value: "operator", label: "Operator" },
              { value: "directory", label: "Directory" },
              { value: "affiliate_site", label: "Affiliate site" },
              { value: "review_site", label: "Review site" },
              { value: "platform", label: "Platform" },
              { value: "unknown", label: "Unknown" },
            ]}
          />
          <SelectField
            label="Priority tier"
            value={form.priority_tier || "secondary"}
            onChange={(v) => update("priority_tier", v)}
            options={[
              { value: "primary", label: "Primary" },
              { value: "secondary", label: "Secondary" },
              { value: "long_tail", label: "Long tail" },
            ]}
          />
        </div>
        <TextArea label="Trust summary" value={form.trust_summary} onChange={(v) => update("trust_summary", v)} rows={2} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Winner visibility (0-100)" type="number" value={form.winner_visibility_score} onChange={(v) => update("winner_visibility_score", v)} />
          <TextField label="Terms clarity (0-100)" type="number" value={form.terms_clarity_score} onChange={(v) => update("terms_clarity_score", v)} />
          <TextField label="Free entry clarity (0-100)" type="number" value={form.free_entry_clarity_score} onChange={(v) => update("free_entry_clarity_score", v)} />
          <TextField label="Extension policy clarity (0-100)" type="number" value={form.extension_policy_clarity_score} onChange={(v) => update("extension_policy_clarity_score", v)} />
        </div>
        <TextArea label="Social proof summary" value={form.social_proof_summary} onChange={(v) => update("social_proof_summary", v)} rows={2} />
        <TextField label="Known risk flags (comma-separated)" value={form.known_risk_flags} onChange={(v) => update("known_risk_flags", v)} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Terms URL" value={form.terms_url || ""} onChange={(v) => update("terms_url", v)} />
          <TextField label="Free entry URL" value={form.free_entry_url || ""} onChange={(v) => update("free_entry_url", v)} />
          <TextField label="Trustpilot URL" value={form.trustpilot_url || ""} onChange={(v) => update("trustpilot_url", v)} />
          <TextField label="Source URL" value={form.source_url || ""} onChange={(v) => update("source_url", v)} />
        </div>
        <TextArea label="Manual review notes" value={form.manual_review_notes || ""} onChange={(v) => update("manual_review_notes", v)} rows={2} />
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="operator-canonical-domain-section">
          <div className="overline text-[var(--cc-gold)] mb-2">Canonical domain (P0 integrity)</div>
          <p className="text-[11px] text-white/50 mb-3 leading-relaxed">
            The canonical domain is used to validate that competition links actually point to this operator&apos;s site. Use aliases (comma-separated) for legitimate alternate domains.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Canonical domain (e.g. operator.com)" value={form.canonical_domain || ""} onChange={(v) => update("canonical_domain", v)} testId="op-canonical-domain" />
            <TextField label="Alias domains (comma-separated)" value={form.known_alias_domains_str ?? (form.known_alias_domains || []).join(", ")} onChange={(v) => update("known_alias_domains_str", v)} />
          </div>
          <TextArea label="Domain review notes" value={form.domain_review_notes || ""} onChange={(v) => update("domain_review_notes", v)} rows={2} />
        </div>
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="operator-ingest-section">
          <div className="overline text-[var(--cc-gold)] mb-2">Auto-ingest config (P0)</div>
          <p className="text-[11px] text-white/50 mb-3 leading-relaxed">
            When enabled, the auto-ingest pipeline can discover new competitions for this operator. Configure the discovery strategy and any URL allow/blocklist patterns.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <CheckField label="Ingest enabled" value={form.ingest_enabled} onChange={(v) => update("ingest_enabled", v)} />
            <SelectField
              label="Ingest strategy"
              value={form.ingest_strategy || "manual_only"}
              onChange={(v) => update("ingest_strategy", v)}
              testId="op-ingest-strategy"
              options={[
                { value: "manual_only", label: "Manual only" },
                { value: "sitemap", label: "Sitemap" },
                { value: "index_page", label: "Index page" },
                { value: "search_page", label: "Search page (later)" },
                { value: "disabled", label: "Disabled" },
              ]}
            />
            <TextField label="Competitions index URL" value={form.competitions_index_url || ""} onChange={(v) => update("competitions_index_url", v)} testId="op-comp-index-url" />
            <TextField label="Sitemap URL" value={form.sitemap_url || ""} onChange={(v) => update("sitemap_url", v)} />
            <TextField label="URL patterns allowlist (comma)" value={form.url_patterns_allowlist_str ?? (form.url_patterns_allowlist || []).join(", ")} onChange={(v) => update("url_patterns_allowlist_str", v)} />
            <TextField label="URL patterns blocklist (comma)" value={form.url_patterns_blocklist_str ?? (form.url_patterns_blocklist || []).join(", ")} onChange={(v) => update("url_patterns_blocklist_str", v)} />
            <TextField label="Rate limit (seconds)" type="number" value={form.rate_limit_seconds ?? 2} onChange={(v) => update("rate_limit_seconds", v)} />
            <CheckField label="Requires JS rendering" value={form.requires_js} onChange={(v) => update("requires_js", v)} />
            <CheckField label="Blocked by Cloudflare" value={form.blocked_by_cloudflare} onChange={(v) => update("blocked_by_cloudflare", v)} />
          </div>
          {(form.last_ingest_status || form.last_ingested_at) && (
            <div className="mt-2 text-[11px] text-white/40">
              Last ingest: <span className="text-white/70">{form.last_ingest_status || "—"}</span>
              {form.last_ingested_at && <> · {new Date(form.last_ingested_at).toLocaleString("en-GB")}</>}
            </div>
          )}
          <TextArea label="Ingest notes" value={form.ingest_notes || ""} onChange={(v) => update("ingest_notes", v)} rows={2} testId="op-ingest-notes" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-white/70">Cancel</button>
          <button type="submit" className="btn-gold px-5 py-2 rounded text-sm">Save</button>
        </div>
      </form>
    </Modal>
  );
};
